import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Search, 
  Download, 
  Upload, 
  Eye, 
  Lock, 
  Globe, 
  Calendar, 
  FileText, 
  Tag,
  Filter,
  Plus,
  Trash2,
  Star,
  BarChart3,
  Users,
  CheckCircle,
  AlertCircle,
  Loader,
  FolderOpen,
  HardDrive,
  Clock,
  TrendingUp,
  Share2,
  MoreVertical,
  Grid,
  List
} from 'lucide-react';
import { mockDatasets } from '../../../mock/data';
import { Dataset } from '../../../types/api';
import toast from 'react-hot-toast';

const DataSets: React.FC = () => {
  const [datasets, setDatasets] = useState<Dataset[]>(mockDatasets);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'all' | 'public' | 'private'>('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [showDatasetDetail, setShowDatasetDetail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // 上传表单状态
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    category: '',
    tags: '',
    type: 'private' as 'public' | 'private',
    file: null as File | null
  });

  // 获取所有分类
  const categories = ['all', ...Array.from(new Set(datasets.map(d => d.category)))];

  // 过滤数据集
  const filteredDatasets = datasets.filter(dataset => {
    const matchesSearch = dataset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dataset.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         dataset.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = selectedType === 'all' || dataset.type === selectedType;
    const matchesCategory = selectedCategory === 'all' || dataset.category === selectedCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 处理上传
  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadForm.name || !uploadForm.file) {
      toast.error('请填写必要信息并选择文件');
      return;
    }

    setIsLoading(true);
    try {
      // 模拟上传API调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newDataset: Dataset = {
        id: `ds_${Date.now()}`,
        name: uploadForm.name,
        description: uploadForm.description,
        category: uploadForm.category || 'general',
        type: uploadForm.type,
        size: uploadForm.file.size,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        tags: uploadForm.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        downloadCount: 0
      };

      setDatasets(prev => [newDataset, ...prev]);
      setShowUploadModal(false);
      setUploadForm({
        name: '',
        description: '',
        category: '',
        tags: '',
        type: 'private',
        file: null
      });
      
      toast.success('数据集上传成功！');
    } catch (error) {
      toast.error('上传失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 下载数据集
  const handleDownload = async (datasetId: string) => {
    try {
      // 模拟下载API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDatasets(prev => prev.map(ds => 
        ds.id === datasetId 
          ? { ...ds, downloadCount: ds.downloadCount + 1 }
          : ds
      ));
      
      toast.success('下载开始');
    } catch (error) {
      toast.error('下载失败');
    }
  };

  // 删除数据集
  const handleDelete = async (datasetId: string) => {
    if (!window.confirm('确定要删除这个数据集吗？')) return;
    
    try {
      // 模拟删除API调用
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setDatasets(prev => prev.filter(ds => ds.id !== datasetId));
      toast.success('数据集已删除');
    } catch (error) {
      toast.error('删除失败');
    }
  };

  // 数据集卡片组件
  const DatasetCard = ({ dataset }: { dataset: Dataset }) => (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <div className="p-6">
        {/* 头部 */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-3">
            <div className={`p-3 rounded-xl text-white ${
              dataset.type === 'public' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-500'
            }`}>
              {dataset.type === 'public' ? <Globe className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                {dataset.name}
              </h3>
              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{dataset.description}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-500">
                <span>创建于 {new Date(dataset.createdAt).toLocaleDateString()}</span>
                <span>•</span>
                <span>{formatFileSize(dataset.size)}</span>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <MoreVertical className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* 标签 */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className={`px-2 py-1 text-xs font-medium rounded-lg ${
            dataset.type === 'public' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-blue-100 text-blue-800'
          }`}>
            {dataset.type === 'public' ? '公开' : '私有'}
          </span>
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg">
            {dataset.category}
          </span>
          {dataset.tags.slice(0, 2).map((tag, index) => (
            <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-lg">
              {tag}
            </span>
          ))}
          {dataset.tags.length > 2 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg">
              +{dataset.tags.length - 2}
            </span>
          )}
        </div>

        {/* 统计信息 */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-center">
          <div>
            <div className="flex items-center justify-center mb-1">
              <Download className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-sm font-medium text-gray-900">{dataset.downloadCount}</div>
            <div className="text-xs text-gray-600">下载次数</div>
          </div>
          <div>
            <div className="flex items-center justify-center mb-1">
              <Calendar className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-sm font-medium text-gray-900">
              {new Date(dataset.createdAt).toLocaleDateString()}
            </div>
            <div className="text-xs text-gray-600">创建时间</div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setSelectedDataset(dataset);
                setShowDatasetDetail(true);
              }}
              className="flex items-center space-x-1 px-3 py-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 text-sm"
            >
              <Eye className="w-4 h-4" />
              <span>详情</span>
            </button>
            
            <button
              onClick={() => handleDownload(dataset.id)}
              className="flex items-center space-x-1 px-3 py-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all duration-200 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>下载</span>
            </button>
          </div>

          <button
            onClick={() => handleDelete(dataset.id)}
            className="flex items-center space-x-1 px-3 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            <span>删除</span>
          </button>
        </div>
      </div>
    </div>
  );

  // 统计数据
  const stats = {
    total: datasets.length,
    public: datasets.filter(d => d.type === 'public').length,
    private: datasets.filter(d => d.type === 'private').length,
    totalSize: datasets.reduce((sum, d) => sum + d.size, 0)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      {/* 顶部标题区 */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <Database className="w-7 h-7 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center">
                <HardDrive className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent leading-tight">
                数据集管理
              </h1>
              <p className="text-gray-600 mt-2 text-base leading-relaxed">管理和共享您的数据集</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="flex items-center space-x-2 px-4 py-2 bg-white/70 backdrop-blur-sm hover:bg-white border border-white/50 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
              <span>{viewMode === 'grid' ? '列表' : '网格'}</span>
            </button>
            
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Plus className="w-4 h-4" />
              <span>上传数据集</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100">总数据集</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Database className="w-6 h-6 text-blue-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100">公开数据集</p>
                <p className="text-2xl font-bold">{stats.public}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Globe className="w-6 h-6 text-green-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100">私有数据集</p>
                <p className="text-2xl font-bold">{stats.private}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Lock className="w-6 h-6 text-purple-200" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100">总存储</p>
                <p className="text-2xl font-bold">{formatFileSize(stats.totalSize)}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <HardDrive className="w-6 h-6 text-orange-200" />
              </div>
            </div>
          </div>
        </div>

        {/* 搜索和筛选区 */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-xl p-6 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* 搜索框 */}
            <div className="lg:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="搜索数据集..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>
            </div>

            {/* 类型筛选 */}
            <div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'all' | 'public' | 'private')}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">所有类型</option>
                <option value="public">公开</option>
                <option value="private">私有</option>
              </select>
            </div>

            {/* 分类筛选 */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">所有分类</option>
                {categories.filter(c => c !== 'all').map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 数据集列表 */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">加载中...</span>
            </div>
          </div>
        ) : filteredDatasets.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <FolderOpen className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无数据集</h3>
            <p className="text-gray-600 mb-4">没有找到符合条件的数据集</p>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all duration-200 mx-auto"
            >
              <Plus className="w-4 h-4" />
              <span>上传第一个数据集</span>
            </button>
          </div>
        ) : (
          <div className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}>
            {filteredDatasets.map(dataset => (
              <DatasetCard key={dataset.id} dataset={dataset} />
            ))}
          </div>
        )}
      </div>

      {/* 上传弹窗 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">上传数据集</h2>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Database className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  数据集名称 *
                </label>
                <input
                  type="text"
                  required
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="输入数据集名称"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  描述
                </label>
                <textarea
                  rows={3}
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="输入数据集描述"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  分类
                </label>
                <input
                  type="text"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="例如：机器学习、图像处理"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  标签
                </label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="用逗号分隔多个标签"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  可见性
                </label>
                <select
                  value={uploadForm.type}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, type: e.target.value as 'public' | 'private' }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  <option value="private">私有</option>
                  <option value="public">公开</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  文件 *
                </label>
                <input
                  type="file"
                  required
                  onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50"
                >
                  {isLoading ? '上传中...' : '上传'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 数据集详情弹窗 */}
      {showDatasetDetail && selectedDataset && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">数据集详情</h2>
                <button
                  onClick={() => setShowDatasetDetail(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Database className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-3">基本信息</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">名称:</span>
                    <span className="ml-2 font-medium">{selectedDataset.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">类型:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      selectedDataset.type === 'public' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedDataset.type === 'public' ? '公开' : '私有'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">分类:</span>
                    <span className="ml-2 font-medium">{selectedDataset.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">大小:</span>
                    <span className="ml-2 font-medium">{formatFileSize(selectedDataset.size)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">创建时间:</span>
                    <span className="ml-2 font-medium">{new Date(selectedDataset.createdAt).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">下载次数:</span>
                    <span className="ml-2 font-medium">{selectedDataset.downloadCount}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">描述</h3>
                <p className="text-sm text-gray-600">{selectedDataset.description}</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">标签</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedDataset.tags.map((tag, index) => (
                    <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-lg">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-3">时间信息</h3>
                <div className="text-sm text-gray-600">
                  <div>创建时间: {new Date(selectedDataset.createdAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataSets;
